import { createSignal, type Accessor } from 'solid-js'
import type { ShopTab } from '~/lib/deep-link'
import { mark } from '~/lib/perf'
import { surfaceWindow, type HydrationGate } from './catalogue-grid'
import type { ClassicReveal } from './classic-reveal'

// The Shop hydration order (GLOSSARY.md; ADR 0041) for one page load: which
// slots of each grid hydrate behind the boot skeleton — the landing tab's
// reveal surface — and when the rest may start: the Shop reveal, which
// resolves `released`. After the reveal, the landing tab's grids enqueue
// first, the other tabs' grids one microtask later, and the currency
// catalogue after both — each tier's native calls queue behind the previous
// tier's in the shared limiter. The Classic grid is in no tier: past its
// surface each slot waits to come within reach (ADR 0051), and off a Classic
// landing its surface waits for the tab's first entry and the Classic reveal
// (ADR 0049).
export interface HydrationOrder {
  classic: HydrationGate
  // Reactive: whether the currency catalogue may hydrate — at once on a
  // top_up landing, where it is the reveal surface; otherwise after the
  // reveal, behind both grid tiers.
  currencyEnabled: Accessor<boolean>
  dailyPicks: HydrationGate
  heroRow: HydrationGate
}

export function createHydrationOrder(input: {
  // The tab Shop lands on: Classic for a tree_id or landskin_id launch,
  // whose target hydrates ahead of everything and whose surface stands in
  // when the target resolves to no visible Classic product (GLOSSARY.md
  // "Shop launch deep link"; ADR 0048).
  landing: ShopTab
  released: Promise<void>
  // The Classic reveal, off a Classic landing.
  classicReveal?: Pick<ClassicReveal, 'entered' | 'released'>
}): HydrationOrder {
  const { classicReveal, landing, released } = input
  // The landing tab's surface hydrates now; after the reveal its grids —
  // the ones with a surface window on that tab — take the first tier.
  const surface = surfaceWindow(landing)
  const later = released.then(() => undefined)
  const last = later.then(() => undefined)
  const [currencyEnabled, setCurrencyEnabled] = createSignal(
    landing === 'currency',
  )
  void last.then(() => setCurrencyEnabled(true))
  void released.then(() =>
    mark('hydration:phase2-released', { landing_tab: landing }),
  )
  // A grid with leading slots on the surface is on the landing tab, so the
  // rest of it takes the first tier after the reveal.
  const gate = (leading: number): HydrationGate => ({
    onSurface: (index) => index < leading,
    released: leading > 0 ? released : later,
  })
  return {
    // Past its surface the Classic grid takes no tier: each slot waits to
    // come within reach (GLOSSARY.md "In-view hydration"; ADR 0051).
    classic: classicReveal
      ? {
          inView: true,
          onSurface: (index) => index < surfaceWindow('classic').classic,
          opened: classicReveal.entered,
          released: classicReveal.released,
        }
      : { ...gate(surface.classic), inView: true },
    currencyEnabled,
    dailyPicks: gate(surface.dailyPicks),
    heroRow: gate(surface.heroRow),
  }
}
